/**
 * Vessel Config to Equipment Mapper
 * READ-ONLY mapper that converts vessel config options to equipment items
 * Enhanced with OPTIONS_TO_EQUIPMENT bridge for comprehensive equipment mapping
 * Does NOT modify vessel config - only reads from it
 */

import { ABSTRACT_REQUIREMENTS, getCurrentEquipmentInstances } from './abstractRequirements.js';
import { getEquipmentFromVesselOptions, debugOptionsToEquipment } from './optionsToEquipmentBridge.js';
import { 
  getImmersionSuitRequirement,
  getThermalProtectiveAidsRequirement,
  getLineThrowingRequirement
} from '../../components/tailwind/settings/wizard/steps/utils/safetyEquipmentLogic.js';

/**
 * Get optional equipment from vessel config arrays
 * ENHANCED: Now uses OPTIONS_TO_EQUIPMENT bridge for comprehensive mapping
 * READ-ONLY - does not modify vessel config
 */
export const getOptionalEquipmentFromVesselConfig = (vesselConfig) => {
  if (!vesselConfig) {
    return [];
  }

  // Use the new OPTIONS_TO_EQUIPMENT bridge for comprehensive mapping
  const bridgeEquipment = getEquipmentFromVesselOptions(vesselConfig);
  
  // Get legacy equipment for backward compatibility
  const legacyEquipment = getLegacyOptionalEquipmentFromVesselConfig(vesselConfig);
  
  // Combine both sources (bridge takes priority)
  const combinedEquipment = [...bridgeEquipment];
  
  // Add legacy equipment that doesn't conflict with bridge equipment
  legacyEquipment.forEach(legacyItem => {
    const bridgeHasItem = bridgeEquipment.some(bridgeItem => 
      bridgeItem.name === legacyItem.name || bridgeItem.id === legacyItem.id
    );
    if (!bridgeHasItem) {
      combinedEquipment.push(legacyItem);
    }
  });

  // Debug log in development
  if (process.env.NODE_ENV === 'development') {
    const debug = debugOptionsToEquipment(vesselConfig);

  }
  
  return combinedEquipment;
};

/**
 * LEGACY FUNCTION - Get optional equipment using old manual mapping
 * This maintains backward compatibility while we transition to OPTIONS_TO_EQUIPMENT bridge
 * @deprecated Use OPTIONS_TO_EQUIPMENT bridge instead
 */
export const getLegacyOptionalEquipmentFromVesselConfig = (vesselConfig) => {
  const optionalEquipment = [];

  // LSA Equipment from SafetyEquipmentTab
  if (vesselConfig.lsaEquipment) {
    vesselConfig.lsaEquipment.forEach(item => {
      const equipmentItem = mapLSAEquipment(item, vesselConfig);
      if (equipmentItem) {
        optionalEquipment.push(equipmentItem);
      }
    });
  }

  // Special Equipment from SafetyEquipmentTab
  if (vesselConfig.specialEquipment) {
    vesselConfig.specialEquipment.forEach(item => {
      const equipmentItem = mapSpecialEquipment(item);
      if (equipmentItem) {
        optionalEquipment.push(equipmentItem);
      }
    });
  }

  // Note: Fixed fire systems and optional equipment now handled by OPTIONS_TO_EQUIPMENT bridge
  
  return optionalEquipment;
};

/**
 * Get abstract requirements with current variants selected
 */
export const getAbstractRequirementsWithVariants = (vesselConfig) => {
  const abstractRequirements = [];

  Object.values(ABSTRACT_REQUIREMENTS).forEach(requirement => {
    // Check if this abstract requirement applies to the current vessel config
    if (!shouldIncludeAbstractRequirement(requirement.id, vesselConfig)) {
      return; // Skip this requirement
    }

    const instances = getCurrentEquipmentInstances(requirement.id, vesselConfig);
    
    abstractRequirements.push({
      ...requirement,
      currentInstances: instances,
      satisfied: instances.length >= requirement.qtyRequired,
      source: 'abstract_requirement'
    });
  });

  return abstractRequirements;
};

/**
 * Check if an abstract requirement should be included based on vessel configuration
 */
const shouldIncludeAbstractRequirement = (abstractRequirementId, vesselConfig) => {
  switch (abstractRequirementId) {
    case 'lsa.recovery.equipment':
      // MOB recovery equipment applies to seagoing vessels only per WBC3 14.7.1
      return vesselConfig.operatingStatus === 'seagoing';
      
    case 'lsa.recovery.pilot.equipment':
      // Pilot recovery equipment applies to pilot boats only per WBC3 Table 27.2.3
      return vesselConfig.vesselType === 'pilot' || vesselConfig.type === 'pilot' || vesselConfig.isPilotBoat;
      
    case 'lsa.pilot.lifelines':
      // Pilot lifelines apply to pilot boats only
      return vesselConfig.vesselType === 'pilot' || vesselConfig.type === 'pilot' || vesselConfig.isPilotBoat;
      
    case 'lsa.mob.prevention.pilot':
      // Pilot fall prevention applies to pilot boats without guardrails
      return (vesselConfig.vesselType === 'pilot' || vesselConfig.type === 'pilot' || vesselConfig.isPilotBoat) && !vesselConfig.hasGuardrails;
      
    case 'lsa.lifejackets.type':
      // Lifejacket type choice applies to all vessels
      return true;
      
    default:
      // Include by default
      return true;
  }
};

/**
 * Map LSA equipment from vessel config
 */
const mapLSAEquipment = (item, vesselConfig = {}) => {
  const lsaMapping = {
    'immersion_suits': {
      id: 'opt_lsa_immersion_suits',
      name: 'Immersion Suits',
      description: 'Thermal protective survival suits'
    },
    'thermal_protective_aids': {
      id: 'opt_lsa_thermal_aids',
      name: 'Thermal Protective Aids (TPA)',
      description: 'For liferaft use'
    },
    'line_throwing_apparatus': {
      id: 'opt_lsa_line_throwing',
      name: 'Line Throwing Apparatus',
      description: 'Rocket line or similar'
    }
    // Note: jason_cradle, scramble_nets, recovery_basket now handled by abstract requirements
  };

  const mapping = lsaMapping[item];
  if (!mapping) return null;

  // Check if this equipment is mandatory based on vessel configuration
  let isMandatory = false;
  let mandatoryReason = '';
  
  if (item === 'immersion_suits') {
    const { immersionSuitsMandatory, immersionSuitsReason } = getImmersionSuitRequirement(vesselConfig);
    isMandatory = immersionSuitsMandatory;
    mandatoryReason = immersionSuitsReason;
  } else if (item === 'thermal_protective_aids') {
    const { tpaMandatory, tpaReason } = getThermalProtectiveAidsRequirement(vesselConfig);
    isMandatory = tpaMandatory;
    mandatoryReason = tpaReason;
  } else if (item === 'line_throwing_apparatus') {
    const { lineThrowingMandatory, lineThrowingReason } = getLineThrowingRequirement(vesselConfig);
    isMandatory = lineThrowingMandatory;
    mandatoryReason = lineThrowingReason;
  }

  return {
    ...mapping,
    category: 'Life-Saving Appliances',
    domain: 'lsa',
    mandatory: isMandatory,
    selected: true,
    qtyPresent: 1,
    qtyRequired: isMandatory ? 1 : 0,
    source: isMandatory ? 'vessel_config_mandatory' : 'vessel_config_lsa',
    reason: mandatoryReason || 'User selected optional equipment'
  };
};

/**
 * Map special equipment from vessel config
 */
const mapSpecialEquipment = (item) => {
  const specialMapping = {
    'winch_lifting_gear': {
      id: 'opt_ops_winch',
      name: 'Winch/Lifting Gear',
      description: 'Requires LOLER inspections and emergency stop familiarisation'
    },
    'gas_detection': {
      id: 'opt_safety_gas_detection',
      name: 'Portable Gas Detection',
      description: 'For enclosed space entry'
    },
    'crane': {
      id: 'opt_ops_crane',
      name: 'Deck Crane',
      description: 'Lifting equipment requiring training and LOLER inspections'
    },
    'dynamic_positioning': {
      id: 'opt_nav_dp_system',
      name: 'Dynamic Positioning System',
      description: 'DP system requiring operator training'
    },
    'motion_compensated_gangway': {
      id: 'opt_ops_gangway',
      name: 'Motion Compensated Gangway',
      description: 'CTV gangway system'
    },
    'fender_systems': {
      id: 'opt_ops_fenders',
      name: 'Active Fender Systems',
      description: 'Deployable fender systems'
    }
  };

  const mapping = specialMapping[item];
  if (!mapping) return null;

  return {
    ...mapping,
    category: 'Operations Equipment',
    domain: 'operations',
    mandatory: false,
    selected: true,
    qtyPresent: 1,
    source: 'vessel_config_special'
  };
};

// Navigation equipment now handled by OPTIONS_TO_EQUIPMENT bridge